/**
 * Helicopter 3D model builder
 * Creates an AH-64D Apache from geometric primitives
 */

import * as THREE from 'three';

export function createHelicopter(): THREE.Group {
  const helicopter = new THREE.Group();

  // Color palette
  const fuselageColor = 0x4b5320; // Olive Drab
  const rotorColor = 0x202020; // Dark for rotors
  const cockpitColor = 0x202428; // Charcoal

  // Materials
  const fuselageMaterial = new THREE.MeshPhongMaterial({
    color: fuselageColor,
    flatShading: true,
  });

  const cockpitMaterial = new THREE.MeshPhongMaterial({
    color: cockpitColor,
    flatShading: true,
    transparent: true,
    opacity: 0.8,
  });

  const rotorMaterial = new THREE.MeshPhongMaterial({
    color: rotorColor,
    flatShading: true,
    transparent: true,
    opacity: 0.3,
  });

  // Main fuselage (angular, blocky)
  const fuselageGeometry = new THREE.BoxGeometry(2, 2, 10);
  const fuselage = new THREE.Mesh(fuselageGeometry, fuselageMaterial);
  fuselage.castShadow = true;
  helicopter.add(fuselage);

  // Cockpit
  const cockpitGeometry = new THREE.BoxGeometry(2, 1.5, 3);
  const cockpit = new THREE.Mesh(cockpitGeometry, cockpitMaterial);
  cockpit.position.set(0, 1, -3);
  cockpit.castShadow = true;
  helicopter.add(cockpit);

  // Tail boom
  const tailGeometry = new THREE.BoxGeometry(0.8, 0.8, 8);
  const tail = new THREE.Mesh(tailGeometry, fuselageMaterial);
  tail.position.set(0, 0.5, 8);
  tail.castShadow = true;
  helicopter.add(tail);

  // Tail rotor
  const tailRotorGeometry = new THREE.BoxGeometry(0.1, 3, 0.5);
  const tailRotor = new THREE.Mesh(tailRotorGeometry, rotorMaterial);
  tailRotor.position.set(1.5, 0.5, 12);
  tailRotor.castShadow = true;
  helicopter.add(tailRotor);

  // Main rotor mast
  const mastGeometry = new THREE.CylinderGeometry(0.2, 0.2, 2, 8);
  const mast = new THREE.Mesh(mastGeometry, fuselageMaterial);
  mast.position.set(0, 2, 0);
  mast.castShadow = true;
  helicopter.add(mast);

  // Longbow radar dome (signature feature)
  const radarGeometry = new THREE.CylinderGeometry(0.8, 0.8, 0.5, 16);
  const radar = new THREE.Mesh(radarGeometry, fuselageMaterial);
  radar.position.set(0, 3.5, 0);
  radar.castShadow = true;
  helicopter.add(radar);

  // Main rotor disk (blurred effect)
  const rotorDiskGeometry = new THREE.CylinderGeometry(8, 8, 0.1, 32);
  const rotorDisk = new THREE.Mesh(rotorDiskGeometry, rotorMaterial);
  rotorDisk.position.set(0, 3, 0);
  helicopter.add(rotorDisk);

  // TADS turret (nose sphere)
  const tadsGeometry = new THREE.SphereGeometry(0.5, 16, 16);
  const tads = new THREE.Mesh(tadsGeometry, cockpitMaterial);
  tads.position.set(0, -0.5, -5);
  tads.castShadow = true;
  helicopter.add(tads);

  // Stub wings with weapon pylons
  const wingGeometry = new THREE.BoxGeometry(8, 0.3, 2);
  const wing = new THREE.Mesh(wingGeometry, fuselageMaterial);
  wing.position.set(0, -0.5, 1);
  wing.castShadow = true;
  helicopter.add(wing);

  // Weapon pylons (left and right)
  const pylonGeometry = new THREE.BoxGeometry(0.5, 0.5, 1.5);
  
  const leftPylon = new THREE.Mesh(pylonGeometry, fuselageMaterial);
  leftPylon.position.set(-3, -0.8, 1);
  leftPylon.castShadow = true;
  helicopter.add(leftPylon);

  const rightPylon = new THREE.Mesh(pylonGeometry, fuselageMaterial);
  rightPylon.position.set(3, -0.8, 1);
  rightPylon.castShadow = true;
  helicopter.add(rightPylon);

  // Landing skids
  const skidGeometry = new THREE.BoxGeometry(0.3, 0.2, 8);
  
  const leftSkid = new THREE.Mesh(skidGeometry, fuselageMaterial);
  leftSkid.position.set(-1.2, -1.5, 0);
  leftSkid.castShadow = true;
  helicopter.add(leftSkid);

  const rightSkid = new THREE.Mesh(skidGeometry, fuselageMaterial);
  rightSkid.position.set(1.2, -1.5, 0);
  rightSkid.castShadow = true;
  helicopter.add(rightSkid);

  // Scale the entire helicopter
  helicopter.scale.set(0.5, 0.5, 0.5);

  return helicopter;
}
